import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PlusCircle, ArrowRight, CalendarClock, Zap } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "../../lib/api";
import { Card, EmptyState, Spinner, Stat, StatusChip, TypeChip, fmtMoney } from "../ui";

export default function CustomerDashboard() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("requests");
  const [history, setHistory] = useState(null);
  const [busyCycle, setBusyCycle] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/portal/overview").then((r) => setData(r.data)).catch(() => setData({ counts: {}, recent: [], upcoming_cycles: [] }));
  }, []);

  useEffect(() => {
    if (tab === "history" && history === null) {
      api.get("/portal/history").then((r) => setHistory(r.data.items)).catch(() => setHistory([]));
    }
  }, [tab]);

  if (!data) return <div className="py-24"><Spinner /></div>;
  const c = data.counts;
  const cycles = data.upcoming_cycles || [];

  const startMarketCheck = async (cyc) => {
    if (busyCycle) return;
    setBusyCycle(cyc.id);
    try {
      const res = await api.post(`/portal/requests/${cyc.request_id}/repeat`, { mode: "market_check" });
      toast.success("Market check started", { description: res.data.request_ref });
      navigate(`/portal/requests/${res.data.id}`);
    } catch (err) {
      toast.error("Could not start market check", { description: fmtErr(err) });
    } finally {
      setBusyCycle(null);
    }
  };

  return (
    <div data-testid="customer-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-outfit text-3xl font-extralight tracking-tight text-white">
            Procurement <span className="text-gold">Dashboard</span>
          </h1>
          <p className="text-sm text-white/50 mt-1">Tell Ajemi what you need — we do the market work.</p>
        </div>
        <Link
          to="/portal/new"
          data-testid="new-procurement-request-btn"
          className="inline-flex items-center gap-2 bg-gold text-ink font-outfit font-medium rounded-full px-6 py-3 hover:bg-goldbright hover:shadow-[0_0_32px_rgba(217,164,65,0.4)] transition-all"
        >
          <PlusCircle className="w-4 h-4" /> New Procurement Request
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <Stat label="Active" value={c.active ?? 0} testId="stat-active" />
        <Stat label="Pending" value={c.pending ?? 0} testId="stat-pending" />
        <Stat label="Approval Needed" value={c.approval ?? 0} testId="stat-approval" />
        <Stat label="Completed" value={c.completed ?? 0} testId="stat-completed" />
        <Stat label="Recurring Programs" value={c.recurring ?? 0} testId="stat-recurring" />
      </div>

      {cycles.length > 0 && (
        <Card title="Upcoming Supply Cycles" className="mb-8 border-gold/25" testId="upcoming-cycles-card">
          <div className="space-y-3">
            {cycles.map((cyc) => (
              <div key={cyc.id} className="flex flex-wrap items-center justify-between gap-3 border border-white/10 rounded-xl p-4" data-testid={`cycle-reminder-${cyc.id}`}>
                <div className="flex items-center gap-3">
                  <CalendarClock className="w-4 h-4 text-gold shrink-0" />
                  <div>
                    <p className="text-sm text-white/85">{cyc.label} — {cyc.product}</p>
                    <p className="text-xs text-white/40 mt-0.5">{cyc.ref} · due {cyc.due_date}</p>
                  </div>
                </div>
                <button
                  onClick={() => startMarketCheck(cyc)}
                  disabled={busyCycle === cyc.id}
                  className="inline-flex items-center gap-2 bg-gold/10 border border-gold/40 text-gold text-xs font-outfit rounded-full px-5 py-2 hover:bg-gold hover:text-ink transition-colors disabled:opacity-50"
                  data-testid={`cycle-market-check-${cyc.id}`}
                >
                  <Zap className="w-3.5 h-3.5" /> Start Market Check
                </button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="flex gap-2 mb-6 border-b border-white/8" data-testid="customer-tabs">
        {[["requests", "Requests"], ["history", "Procurement History"]].map(([key, label]) => (
          <button
            key={key}
            data-testid={`customer-tab-${key}`}
            onClick={() => setTab(key)}
            className={`px-5 py-3 text-sm font-outfit border-b-2 transition-colors -mb-px ${tab === key ? "border-gold text-gold" : "border-transparent text-white/50 hover:text-white"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "requests" && (
        <Card title="Procurement Requests" testId="customer-requests-card">
          {data.recent.length === 0 ? (
            <EmptyState title="No requests yet" text="Submit your first procurement request and Ajemi will take it from there.">
              <Link to="/portal/new" className="inline-flex items-center gap-2 text-gold text-sm hover:text-goldbright transition-colors" data-testid="empty-new-request-link">
                Start a request <ArrowRight className="w-4 h-4" />
              </Link>
            </EmptyState>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="customer-requests-table">
                <thead>
                  <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-white/35 border-b border-white/8">
                    <th className="pb-3 pr-4">Request</th>
                    <th className="pb-3 pr-4">Product</th>
                    <th className="pb-3 pr-4">Type</th>
                    <th className="pb-3 pr-4">Status</th>
                    <th className="pb-3">Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent.map((r) => (
                    <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                      <td className="py-4 pr-4">
                        <Link to={`/portal/requests/${r.id}`} className="text-gold hover:text-goldbright font-outfit" data-testid={`request-link-${r.ref}`}>
                          {r.ref}
                        </Link>
                      </td>
                      <td className="py-4 pr-4 text-white/75">{r.product}</td>
                      <td className="py-4 pr-4"><TypeChip type={r.type} /></td>
                      <td className="py-4 pr-4"><StatusChip status={r.status} /></td>
                      <td className="py-4 text-white/40">{new Date(r.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === "history" && (
        <Card title="Procurement History" testId="customer-history-card">
          {history === null ? (
            <div className="py-12"><Spinner /></div>
          ) : history.length === 0 ? (
            <EmptyState title="No history yet" text="Your completed and in-progress procurements will build your purchasing history here." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="customer-history-table">
                <thead>
                  <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-white/35 border-b border-white/8">
                    <th className="pb-3 pr-4">Date</th>
                    <th className="pb-3 pr-4">Request</th>
                    <th className="pb-3 pr-4">Product</th>
                    <th className="pb-3 pr-4">Volume</th>
                    <th className="pb-3 pr-4">Location</th>
                    <th className="pb-3 pr-4">Status</th>
                    <th className="pb-3 pr-4">Approved Price</th>
                    <th className="pb-3 pr-4">Supplier</th>
                    <th className="pb-3">Delivered</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                      <td className="py-4 pr-4 text-white/40 whitespace-nowrap">{new Date(h.date).toLocaleDateString()}</td>
                      <td className="py-4 pr-4">
                        <Link to={`/portal/requests/${h.id}`} className="text-gold hover:text-goldbright font-outfit" data-testid={`history-link-${h.ref}`}>{h.ref}</Link>
                      </td>
                      <td className="py-4 pr-4 text-white/75">{h.product}</td>
                      <td className="py-4 pr-4 text-white/75 whitespace-nowrap">{Number(h.quantity || 0).toLocaleString()} {h.unit}</td>
                      <td className="py-4 pr-4 text-white/60 max-w-[160px] truncate">{h.location}</td>
                      <td className="py-4 pr-4"><StatusChip status={h.status} /></td>
                      <td className="py-4 pr-4 text-white/75 whitespace-nowrap">
                        {h.approved_price != null ? `${fmtMoney(h.approved_price, h.currency)}/${h.unit.replace(/s$/, "")}` : "—"}
                      </td>
                      <td className="py-4 pr-4 text-white/60">{h.supplier || "—"}</td>
                      <td className="py-4 text-white/60 whitespace-nowrap">{h.delivered ? `${Number(h.delivered).toLocaleString()} ${h.unit}` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
